/**
 * useDealActiveConversations - Conversas ativas do contato de um negócio
 *
 * Usado no modal de "Marcar como perdido" para permitir encerrar, na mesma
 * confirmação, as conversas ativas (open/pending) vinculadas ao vendedor do
 * negócio. Toda a regra de visibilidade é aplicada server-side pela RPC
 * `get_deal_active_conversations` (SECURITY DEFINER).
 *
 * RELEASE: 2026-08-07 v1.0.0
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('DealActiveConversations');

// Conversa ativa retornada pela RPC
export interface DealActiveConversation {
  conversation_id: string;
  status: string;
  last_message_preview: string | null;
  last_message_at: string | null;
  unread_count: number;
  inbox_id: string;
  inbox_name: string | null;
  channel_type: string | null;
  assigned_to: string | null;
  assigned_agent_name: string | null;
  assigned_agent_avatar: string | null;
}

/**
 * Busca as conversas ativas do negócio (lazy: só quando `enabled` for true).
 */
export function useDealActiveConversations(
  dealId: string | null,
  enabled: boolean = true,
) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['deal-active-conversations', dealId],
    queryFn: async (): Promise<DealActiveConversation[]> => {
      if (!dealId) return [];

      logger.debug('Buscando conversas ativas do negócio:', dealId);

      const { data, error } = await (supabase.rpc as any)(
        'get_deal_active_conversations',
        {
          p_deal_id: dealId,
          p_tenant_hint: currentTenant?.id ?? null,
        },
      );

      if (error) {
        logger.error('Erro ao buscar conversas ativas:', error.message);
        throw error;
      }

      return (data || []) as DealActiveConversation[];
    },
    enabled: !!dealId && !!currentTenant?.id && enabled,
    staleTime: 15 * 1000,
  });
}
